import { produce } from 'immer';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import newsletterBannerMobileImg from '../../../../../common/assets/images/marketing/newsletter-banner-mobile.png';
import newsletterBannerSmallImg from '../../../../../common/assets/images/marketing/newsletter-banner-small.png';
import newsletterBannerWideImg from '../../../../../common/assets/images/marketing/newsletter-banner-wide.png';
import { showErrorToast } from '../../../../../common/global/helpers';
import Icons from '../../../../../common/widgets/Icons';
import { CustomerInfoContext } from '../../../contexts/CustomerInfoContext';
import { OptInSourceTypes, OptInTypes } from '../../../enums';
import { resources } from '../../../resources/resources';
import { optInService } from '../../../services/opt-in';
import MarketingHomePageBanner from './MarketingHomePageBanner';

type Status = 'idle' | 'loading' | 'success' | 'error';
type MarketingBannerVariant = 'wide' | 'small';

interface NewsletterBannerProps {
  variant: MarketingBannerVariant;
}

export const showNewsletterErrorToast = (
  title: string,
  subtitle: string,
  options?: {
    duration?: number;
    showCloseButton?: boolean;
  },
) => {
  const { duration = 10000, showCloseButton = false } = options || {};

  showErrorToast(
    '',
    showCloseButton,
    undefined,
    duration,
    <div>
      <div className="newsletter-toast">
        <h4>{title}</h4>
        <small>{subtitle}</small>
      </div>
    </div>,
    'newsletter-toast-root',
  );
};

export default function NewsletterBanner({
  variant,
}: Readonly<NewsletterBannerProps>) {
  const { t: translate } = useTranslation();
  const isProcessingRef = useRef(false);
  const { customerInfo, setCustomerInfo } = useContext(CustomerInfoContext);
  const [status, setStatus] = useState<Status>('idle');

  const desktopImage =
    variant === 'wide' ? newsletterBannerWideImg : newsletterBannerSmallImg;

  const handleConsent = async () => {
    if (isProcessingRef.current) return;

    isProcessingRef.current = true;
    setStatus('loading');

    try {
      const response = await optInService.consent(
        OptInTypes.NewsletterMarketing,
        true,
        OptInSourceTypes.HomePage,
      );

      if (response.hasErrors) {
        showNewsletterErrorToast(
          translate(resources.newsletter.banners.error.title),
          translate(resources.newsletter.banners.error.subtitle),
        );
        setStatus('error');
        return;
      }

      setStatus('success');
    } finally {
      isProcessingRef.current = false;
    }
  };

  useEffect(() => {
    return () => {
      if (status === 'success') {
        setCustomerInfo(
          produce(customerInfo, draft => {
            draft.showNewsletterBanners = false;
            draft.showNewsletterPopup = false;
          }),
        );
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  if (status === 'success') {
    return (
      <MarketingHomePageBanner
        variant={variant}
        title={
          <>
            {translate(resources.newsletter.banners.success.title)}{' '}
            <Icons.PartyEmoji />
          </>
        }
        description={translate(resources.newsletter.banners.success.subtitle)}
        desktopImage={desktopImage}
        mobileImage={newsletterBannerMobileImg}
        isSuccess
      />
    );
  }

  const footer = (
    <small>
      {'*'}
      {translate(resources.newsletter.banners.footer.text1)}{' '}
      <Link
        to="/politica-prelucrare-date-personale"
        target="_blank"
        rel="noopener noreferrer"
      >
        {translate(resources.newsletter.banners.footer.privacyPolicyLink)}
      </Link>
      {translate(resources.newsletter.banners.footer.text2)}{' '}
      <Link to="/my-account/preferences">
        {translate(
          resources.newsletter.banners.footer.communicationPreferencesLink,
        )}
      </Link>
      {customerInfo.languageCode === 'ro' ? '' : ' '}
      {translate(resources.newsletter.banners.footer.text3)}
    </small>
  );

  return (
    <MarketingHomePageBanner
      variant={variant}
      title={translate(resources.newsletter.banners.title)}
      description={translate(resources.newsletter.banners.description)}
      actionLabel={translate(resources.newsletter.banners.action)}
      desktopImage={desktopImage}
      mobileImage={newsletterBannerMobileImg}
      footer={footer}
      onAction={handleConsent}
      loading={status === 'loading'}
    />
  );
}
